<script setup lang="ts">
import CardsBlog from "@/components/Blog/Cards.vue";
import CardsBlogMobile from "@/components/Blog/CardsMobile.vue";
import LastComments from "@/components/Blog/LastComments.vue";
import LastCommentsMobile from "@/components/Blog/LastCommentsMobile.vue";
import BannerAssineHorizontal from "@/components/HomePage/BannerAssineHorizontal.vue";
import translaters from "@/tradutors/blogs";
import { isMobile } from "@/shared/utils/mobile";
import PostListItemMobile from "@/components/HomePage/PostListItemMobile.vue";
import api from "@/api";

const storeLocale = localeStore();

const route = useRoute();

const canonical = useCanonicalUrl();
const { data } = await useAsyncData("blogsall" + "-" + route.params.language, async () => {
  const blogsAll = await api
    .get(
      `v1/blog/getBlogs?search=&language=${
        route.params.language ?? ""
      }&limit=14&mobile=${isMobile()}`
    )
    .then(({ data }) => data.blogs);
  return blogsAll;
});

if (!data.value)
  throw createError({ statusCode: 404, statusMessage: "Page Not Found" });

definePageMeta({
  middleware: ["not-found-lang"],
});

const blogs = ref<Array<any>>(data.value);
const blogsHorizontais = ref<any>([]);
const primeirosTresBlogs = ref<any>([]);
const segundoTresBlogs = ref<any>([]);
const promotionBlogs = ref<any>([]);

const lastsBlogs = ref<any>([]);

// Grade desktop contínua: mescla os grupos para fluir 2/linha (769-992px) ou
// 3/linha (>=992px) sem a célula vazia que sobra quando cada grupo de 3 vira
// uma .row própria em layout de 2 colunas.
const desktopGridBlogs = computed(() => [
  ...segundoTresBlogs.value,
  ...promotionBlogs.value,
  ...lastsBlogs.value,
]);

// Limiar de viewport (< 992px) só para os COMENTÁRIOS: nessa faixa mostramos o
// carrossel mobile abaixo do carrossel de blogs. Os cards continuam usando
// isMobile() (770). Inicia false para casar com o SSR (desktop).
const isMobileView = ref(false);
let mqlTablet: MediaQueryList | null = null;
const handleTabletChange = (e: MediaQueryListEvent) => {
  isMobileView.value = e.matches;
};
const linkBannerComment = ref({ image: "", url: "" });
const limit = ref(14);
const coverBanner = ref({ image: "", url: "" });
const bannerAssineHorizontal = ref("");
const search = ref("");
const searchText = ref(false);
const searchTextKeyup = ref(false);

const seo = await useSeoConfig(String(route.name), String(route.params.language));

useHead({
  templateParams: {
    subTitle: null,
    subPage: seo.value?.title || "Blog",
    page_url: canonical,
    page_description:
      seo.value?.description ||
      (String(route.params.language) == "pt-br"
        ? `Fique por dentro do melhor do universo masculino com diversas matérias!`
        : `Stay on top of the best of the male universe with several subjects!`),
    page_image: shareImage(seo.value?.image),
    page_keywords:
      seo.value?.keywords ||
      "bella, bella da semana, bela da semana, bella semana, bela semana, belladasemana, bella site, bella da semana site, bella blog",
    page_locale: route.params.language as string,
    page_url_pt: "https://belladasemana.com.br/pt-br/blog",
    page_url_en: "https://belladasemana.com.br/en/blog",
    page_canonical: canonical,
  },
});

const translate = computed(() =>
  route.params.language == "pt-br" ? translaters.portugues : translaters.ingles
);

function recortarBlogs() {
  blogsHorizontais.value = blogs.value.slice(0, 2);
  primeirosTresBlogs.value = blogs.value.slice(2, 5);
  segundoTresBlogs.value = blogs.value.slice(5, 8);
  promotionBlogs.value = blogs.value.slice(8, 11);
  lastsBlogs.value = blogs.value.slice(11);
}

// Recorta de forma síncrona (SSR + cliente) para que os cards saiam no HTML inicial
recortarBlogs();

onMounted(() => {
  getBannerAssineHorizontal();
  getBannerArroba(1);
  if (isMobile()) {
    getBannerArroba(2);
  }

  mqlTablet = window.matchMedia("(max-width: 991.98px)");
  isMobileView.value = mqlTablet.matches;
  mqlTablet.addEventListener("change", handleTabletChange);
});

onBeforeUnmount(() => {
  mqlTablet?.removeEventListener("change", handleTabletChange);
});


async function getDados() {
  await getBlogs();
  recortarBlogs();
  if (search.value != "") {
    searchTextKeyup.value = true;
  }
}
async function getBlogs() {
  await api
    .get(
      `v1/blog/getBlogs?search=${search.value}&language=${
        route.params.language ?? ""
      }&limit=${limit.value}&mobile=${isMobile()}`
    )
    .then(({ data }) => {
      blogs.value = data.blogs;
    });
}
async function readMore() {
  limit.value += 3;
  await getDados();
}
async function getBannerArroba(local: number) {
  await api
    .get(`v1/home/getBannerArroba?language=${route.params.language ?? ""}`)
    .then(({ data }) => {
      if (local == 1) {
        linkBannerComment.value = data.bannerArroba;
      } else {
        coverBanner.value = data.bannerArroba;
        promotionBlogs.value.unshift({
          id: 1,
          promotion: true,
          cover: coverBanner.value,
        });
      }
    });
}
async function getBannerAssineHorizontal(changeLanguage = false) {
  if (!sessionStorage.getItem("bannerAssineHorizontal") || changeLanguage)
    api
      .get(
        `v1/home/getBannerSuper?mobile=${isMobile()}&language=${
         route.params.language
        }`
      )
      .then(({ data }) => {
        bannerAssineHorizontal.value = data.bannerAssineHorizontal;
        sessionStorage.setItem(
          "bannerAssineHorizontal",
          JSON.stringify(bannerAssineHorizontal.value)
        );
      });
  else
    bannerAssineHorizontal.value = JSON.parse(
      sessionStorage.getItem("bannerAssineHorizontal") ?? ""
    );
}
function searchColumns(message: any) {
  if (search.value != "") {
    searchText.value = true;
    searchTextKeyup.value = true;
    limit.value = 14;
    getDados();
  } else {
    searchText.value = false;
    return message({
      message: {
        show: true,
        title:route.params.language == "pt-br" ? "Aviso" : "Notice",
        textButton:route.params.language == "pt-br" ? "Fechar" : "Close",
        textBoby:
         route.params.language == "pt-br"
            ? "Informe algum termo para busca!"
            : "Enter a search term",
      },
    });
  }
}
function clearSearchText() {
  searchTextKeyup.value = false;
}

watch(
  () => search.value,
  (value) => {
    if (value == "") {
      searchText.value = false;
      limit.value = 14;
      getDados();
    }
  }
);
</script>
<template>
  <NuxtLayout #="{ showModalMessages }">
    <div class="slot-body">
      <h1 class="visually-hidden">{{ route.params.language === 'pt-br' ? 'Blog do Bella: Notícias e Conteúdo do Universo Masculino' : 'Bella Blog: News and Male Universe Content' }}</h1>
      <div v-if="!isMobile()" class="box-gray"></div>
      <div class="container">
        <div class="row pt-5 align-items-center">
          <div class="col-lg-3 col-7 order-1 order-lg-1">
            <div class="title mt-4">
              {{ translate.title }}
            </div>
          </div>
          <div class="col-lg-5 col-12 order-3 order-lg-2">
            <span class="subtitle">
              {{ translate.subTitle }}
            </span>
          </div>
          <div
            class="col-lg-4 col-5 d-flex align-items-center box-input order-2 order-lg-3"
          >
            <input
              type="text"
              name="search"
              id="search-columns"
              :placeholder="translate.searchPlaceholder"
              v-model="search"
              @keyup.enter="searchColumns(showModalMessages)"
              @keyup="clearSearchText"
            />
            <font-awesome
              :icon="['fas', 'search']"
              size="sm"
              @click="searchColumns(showModalMessages)"
            />
          </div>
        </div>
        <div class="row pt-5 row-middle">
          <div class="col-lg-9" v-if="searchText">
            <div class="search-div" v-if="searchTextKeyup">
              <br />{{ translate.searchTerm }} <strong>{{ search }}</strong
              ><br /><br />
            </div>
            <div class="search-div" v-if="searchTextKeyup && !blogs.length">
              {{ translate.searchNoRecords }}<br /><br />
            </div>
            <div class="records-div">
              <PostListItemMobile
                :posts="blogs"
                :home="false"
                v-if="isMobile() && blogs.length"
              />
              <CardsBlog :posts="blogs" v-else-if="blogs.length" />
            </div>
            <LastCommentsMobile :translate="translate" v-if="isMobileView" />
          </div>
          <div v-else class="col-lg-9">

            <CardsBlog
              v-if="blogsHorizontais.length"
              :posts="blogsHorizontais[0]"
              :horizontal="true"
            />

            <CardsBlog
              v-if="blogsHorizontais.length"
              :posts="blogsHorizontais[1]"
              :horizontal="true"
            />

            <CardsBlogMobile
              v-if="primeirosTresBlogs.length"
              :posts="primeirosTresBlogs"
            />
            <LastCommentsMobile :translate="translate" v-if="isMobileView" />

            <template v-if="isMobile()">
              <PostListItemMobile
                :posts="segundoTresBlogs"
                :home="false"
                v-if="segundoTresBlogs.length"
              />
              <PostListItemMobile
                :posts="promotionBlogs"
                :home="false"
                v-if="promotionBlogs.length"
              />
              <PostListItemMobile
                :posts="lastsBlogs"
                :home="false"
                v-if="lastsBlogs.length"
              />
            </template>
            <CardsBlog
              v-else-if="desktopGridBlogs.length"
              :posts="desktopGridBlogs"
            />
          </div>
          <div class="col-lg-3">
            <LastComments :translate="translate" v-if="!isMobileView" />
            <div class="card-banner-promotion" v-if="!isMobileView">
              <a :href="linkBannerComment.url">
                <img :src="linkBannerComment.image" alt="Banner" />
              </a>
            </div>
          </div>
        </div>
        <div
          class="row d-flex align-items-center justify-content-center"
          v-if="blogs.length > 11"
        >
          <button
            type="button"
            class="more-blog my-5 p-3 col-lg-3 col-8"
            @click="readMore"
          >
            {{ translate.readMore }}
            <font-awesome :icon="['fas', 'arrow-down']" />
          </button>
        </div>
        <div class="row pb-5 d-flex align-items-center justify-content-center">
          <BannerAssineHorizontal :path="bannerAssineHorizontal" />
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<style scoped lang="css">
.slot-body {
  margin-top: 65px !important;
  background-color: #fff;
}

.box-gray {
  background-color: #f8f8f8;
  width: 100%;
  height: 251px;
  position: absolute;
}

.title {
  color: #000000;
  font-family: "Bebas-Neue-Light";
  font-size: 64px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 77px;
}

.subtitle {
  height: 56px;
  color: #5b5858;
  font-family: "Roboto-Medium";
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 28px;
}

#search-columns {
  padding: 0 !important;
  padding-left: 30px !important;
  font-size: 1rem !important;
  background-color: #fff !important;
  border: 1px solid #ced4da !important;
  border-radius: 30px !important;
  height: 51px !important;
  width: 100%;
}

.box-input {
  margin-top: 6px;
}

.box-input svg {
  position: absolute;
  right: 45px;
  z-index: 9;
  color: #b20;
  cursor: pointer;
}

.more-blog {
  font-weight: 500;
  background-color: black !important;
  border: 1px solid black !important;
  border-radius: 30px !important;
  color: white;
  font-family: "Exo-2";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 19px;
}

.more-blog svg {
  color: #b20;
  margin-left: 8px;
}

.card-banner-promotion img {
  width: 288px;
  height: 260px;
  margin-top: 3rem;
  object-fit: cover;
}

.search-div {
  top: -70px;
  position: absolute;
}

.records-div {
  top: -25px;
}

/* regras mobile 769vp */
@media screen and (max-width: 769px) {
  .title {
    color: #000000;
    font-family: "Bebas-Neue-Light";
    font-size: 40px;
    font-weight: 300;
    letter-spacing: 0;
    line-height: 58px;
  }
  .subtitle {
    color: #5b5858;
    font-family: "Roboto-Regular";
    font-size: 16px;
    letter-spacing: 0;
    line-height: 19px;
  }

  .card-banner-promotion img {
    width: 100%;
    height: unset;
  }

  #search-columns {
    height: 38px !important;
    padding-left: 10px !important;
    font-size: 0.7rem !important;
    background-color: #eaeaea !important;
  }

  .box-input svg {
    right: 20px;
    z-index: 9;
  }

  .row-middle {
    padding-top: 20px !important;
  }

  .search-div {
    top: 0px;
    position: relative;
  }
}
</style>
